import { ReactNode, createContext, useEffect, useContext, useMemo, useCallback, useState } from 'react';
// hooks
import useLocalStorage from '../../hooks/useLocalStorage';
// utils
import localStorageAvailable from '../../utils/localStorageAvailable';
//
import { defaultSettings } from './config-setting';
import { SettingsContextProps, ThemeLayoutValue } from './types';
import { defaultPreset, getPresets, presetsOption } from './presets';
// ----------------------------------------------------------------------

const initialState: SettingsContextProps = {
  ...defaultSettings,
  // Mode
  onToggleMode: () => {},
  onChangeMode: () => {},
  // Direction
  onToggleDirection: () => {},
  onChangeDirection: () => {},
  onChangeDirectionByLang: () => {},
  // Layout
  onToggleLayout: () => {},
  onChangeLayout: () => {},
  layoutUeberschreibung: null,
  onLayoutUeberschreiben: () => {},
  gespeichertesLayout: defaultSettings.themeLayout,
  layoutWahlZaehler: 0,
  // Contrast
  onToggleContrast: () => {},
  onChangeContrast: () => {},
  // Color
  onChangeColorPresets: () => {},
  presetsColor: defaultPreset,
  presetsOption: [],
  // Stretch
  onToggleStretch: () => {},
  // Reset
  onResetSetting: () => {},
};

// ----------------------------------------------------------------------

export const SettingsContext = createContext(initialState);

export const useSettingsContext = () => {
  const context = useContext(SettingsContext);

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider');

  return context;
};

// ----------------------------------------------------------------------

type SettingsProviderProps = {
  children: ReactNode;
};

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useLocalStorage('settings', defaultSettings);

  const storageAvailable = localStorageAvailable();

  const langStorage = storageAvailable ? localStorage.getItem('i18nextLng') : '';

  const isArabic = langStorage === 'ar';

  // Mode
  const onToggleMode = useCallback(() => {
    const themeMode = settings.themeMode === 'light' ? 'dark' : 'light';
    setSettings({ ...settings, themeMode });
  }, [setSettings, settings]);

  const onChangeMode = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeMode = event.target.value;
      setSettings({ ...settings, themeMode });
    },
    [setSettings, settings],
  );

  // Direction
  const onToggleDirection = useCallback(() => {
    const themeDirection = settings.themeDirection === 'rtl' ? 'ltr' : 'rtl';
    setSettings({ ...settings, themeDirection });
  }, [setSettings, settings]);

  const onChangeDirection = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeDirection = event.target.value;
      setSettings({ ...settings, themeDirection });
    },
    [setSettings, settings],
  );

  const onChangeDirectionByLang = useCallback(
    (lang: string) => {
      const themeDirection = lang === 'ar' ? 'rtl' : 'ltr';
      setSettings({ ...settings, themeDirection });
    },
    [setSettings, settings],
  );

  useEffect(() => {
    if (isArabic) {
      onChangeDirectionByLang('ar');
    }
    // eslint-disable-line
  }, [isArabic]);

  // Layout
  // RITA-1585: Eine Seite kann das Menü vorübergehend anders darstellen (Handbuch offen → schmal), ohne die
  // gespeicherte Einstellung zu ändern. Die eigene Umschaltung des Nutzers hebt die Überschreibung auf und rechnet
  // von der sichtbaren Darstellung aus (wer das schmale Menü breit klickt, bekommt es breit).
  const [layoutUeberschreibung, setLayoutUeberschreibung] = useState<ThemeLayoutValue | null>(null);
  const wirksamesLayout = layoutUeberschreibung ?? settings.themeLayout;
  // Jede eigene Umschaltung zählt; eine Seite, die automatisch umschaltet, hört danach bis zu ihrem Schließen auf
  // (KI-Review !803: Start „mini“, Leiste offen, Nutzer schaltet breit – sonst würde die Automatik erneut greifen).
  const [layoutWahlZaehler, setLayoutWahlZaehler] = useState(0);
  const eigeneWahl = useCallback(() => {
    setLayoutUeberschreibung(null);
    setLayoutWahlZaehler((alt) => alt + 1);
  }, []);

  const onToggleLayout = useCallback(() => {
    const themeLayout = wirksamesLayout === 'vertical' ? 'mini' : 'vertical';
    eigeneWahl();
    setSettings({ ...settings, themeLayout });
  }, [setSettings, settings, wirksamesLayout, eigeneWahl]);

  // Nur der Nutzer zählt (Einstellungsdialog); der Start-Effekt unten spielt die Vorgabe ohne Zählen ein
  // (Kollegenreview !803: sonst sperrte der Start bei schon offenem Handbuch die spätere Anpassung).
  const layoutAnwenden = useCallback(
    (themeLayout: string) => {
      setSettings({ ...settings, themeLayout });
    },
    [setSettings, settings],
  );

  const onChangeLayout = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      eigeneWahl();
      layoutAnwenden(event.target.value);
    },
    [layoutAnwenden, eigeneWahl],
  );

  const onLayoutUeberschreiben = useCallback((layout: ThemeLayoutValue | null) => {
    setLayoutUeberschreibung(layout);
  }, []);

  // Contrast
  const onToggleContrast = useCallback(() => {
    const themeContrast = settings.themeContrast === 'default' ? 'bold' : 'default';
    setSettings({ ...settings, themeContrast });
  }, [setSettings, settings]);

  const onChangeContrast = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeContrast = event.target.value;
      setSettings({ ...settings, themeContrast });
    },
    [setSettings, settings],
  );

  // Color
  const onChangeColorPresets = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const themeColorPresets = event.target.value;
      setSettings({ ...settings, themeColorPresets });
    },
    [setSettings, settings],
  );

  // Stretch
  const onToggleStretch = useCallback(() => {
    const themeStretch = !settings.themeStretch;
    setSettings({ ...settings, themeStretch });
  }, [setSettings, settings]);

  // Reset
  const onResetSetting = useCallback(() => {
    eigeneWahl();
    setSettings(defaultSettings);
  }, [setSettings, eigeneWahl]);

  useEffect(() => {
    if (defaultSettings.themeMode) {
      onChangeMode({ target: { value: defaultSettings.themeMode } } as any);
    }
    if (defaultSettings.themeDirection) {
      onChangeDirection({ target: { value: defaultSettings.themeDirection } } as any);
    }
    if (defaultSettings.themeContrast) {
      onChangeContrast({ target: { value: defaultSettings.themeContrast } } as any);
    }
    if (defaultSettings.themeLayout) {
      layoutAnwenden(defaultSettings.themeLayout);
    }
    if (defaultSettings.themeColorPresets) {
      onChangeColorPresets({ target: { value: defaultSettings.themeColorPresets } } as any);
    }
    if (defaultSettings.themeStretch) {
      onToggleStretch();
    }
    // eslint-disable-line
  }, [defaultSettings]);

  const memoizedValue = useMemo(
    () => ({
      ...settings,
      themeLayout: wirksamesLayout,
      // Mode
      onToggleMode,
      onChangeMode,
      // Direction
      onToggleDirection,
      onChangeDirection,
      onChangeDirectionByLang,
      // Layout
      onToggleLayout,
      onChangeLayout,
      layoutUeberschreibung,
      onLayoutUeberschreiben,
      gespeichertesLayout: settings.themeLayout,
      layoutWahlZaehler,
      // Contrast
      onChangeContrast,
      onToggleContrast,
      // Stretch
      onToggleStretch,
      // Color
      onChangeColorPresets,
      presetsOption,
      presetsColor: getPresets(settings.themeColorPresets),
      // Reset
      onResetSetting,
    }),
    [
      settings,
      wirksamesLayout,
      // Mode
      onToggleMode,
      onChangeMode,
      // Direction
      onToggleDirection,
      onChangeDirection,
      onChangeDirectionByLang,
      // Layout
      onToggleLayout,
      onChangeLayout,
      layoutUeberschreibung,
      onLayoutUeberschreiben,
      layoutWahlZaehler,
      onChangeContrast,
      // Contrast
      onToggleContrast,
      // Stretch
      onToggleStretch,
      // Color
      onChangeColorPresets,
      // Reset
      onResetSetting,
    ],
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
